import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { EmptyState } from "../components/ui-bits";
import { FolderOpen, MapPinned, Layers, Trash2 } from "lucide-react";
import { PROJECT_TYPE_LABELS } from "../lib/constants";
import { toast } from "sonner";

export default function Projects() {
  const [projects, setProjects] = useState(null);
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (loading) return;
    if (!user) {
      navigate("/login?next=/projects");
      return;
    }
    api.get("/projects").then(({ data }) => setProjects(data)).catch(() => setProjects([]));
  }, [user, loading, navigate]);

  const remove = async (id) => {
    try {
      await api.delete(`/projects/${id}`);
      setProjects(projects.filter((p) => p.id !== id));
      toast.success("Project deleted");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-serif text-3xl text-[#0B1F3A]">My projects</h1>
          <p className="text-sm text-[#475569] mt-1">Every saved roadmap, ready to reopen, regenerate or share.</p>
        </div>
        <Link to="/wizard" className="bg-[#1D4ED8] text-white rounded-md px-4 py-2.5 text-sm font-medium hover:bg-[#1638A8]" data-testid="projects-new-btn">Map a new project</Link>
      </div>

      {projects === null && (
        <div className="grid sm:grid-cols-2 gap-4 mt-8">
          {[1, 2].map((i) => <div key={i} className="h-40 bg-[#0B1F3A]/10 rounded-xl animate-pulse" />)}
        </div>
      )}

      {projects?.length === 0 && (
        <div className="mt-8 bg-white border border-[#0B1F3A]/10 rounded-xl">
          <EmptyState icon={FolderOpen} title="No projects yet" subtitle="Map your first project in 60 seconds — no regulatory knowledge needed."
            action={<Link to="/wizard" className="bg-[#1D4ED8] text-white rounded-md px-5 py-2.5 text-sm font-medium" data-testid="projects-empty-wizard-btn">Map my first project</Link>} />
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-4 mt-8">
        {projects?.map((p) => (
          <div key={p.id} className="bg-white border border-[#0B1F3A]/10 rounded-xl p-5 hover:shadow-md transition-shadow flex flex-col" data-testid={`project-card-${p.id}`}>
            <h3 className="font-serif font-semibold text-[#0B1F3A]">{p.name}</h3>
            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-[#475569]">
              <span className="inline-flex items-center gap-1"><MapPinned size={12} /> {p.answers?.state}{p.answers?.district ? ` · ${p.answers.district}` : ""}</span>
              <span className="inline-flex items-center gap-1"><Layers size={12} /> {PROJECT_TYPE_LABELS[p.answers?.project_type] || p.answers?.project_type}</span>
            </div>
            <p className="text-xs text-[#475569] mt-1">Created {new Date(p.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}{p.is_demo_seed ? " · Demo project" : ""}</p>
            <div className="flex gap-2 mt-4 pt-3 border-t border-[#0B1F3A]/10">
              <Link to={`/projects/${p.id}`} className="flex-1 text-center bg-[#1D4ED8] text-white rounded-md py-2 text-sm font-medium hover:bg-[#1638A8]" data-testid={`project-open-${p.id}`}>Open roadmap</Link>
              {!p.is_demo_seed && (
                <button onClick={() => remove(p.id)} className="px-3 border border-[#0B1F3A]/20 rounded-md text-[#475569] hover:text-red-600 hover:border-red-300" data-testid={`project-delete-${p.id}`} aria-label="Delete project">
                  <Trash2 size={15} />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
